import {
  Person,
  SwapHoriz,
  Notes,
  Build,
  Event,
  ArrowForward,
  InsertDriveFile,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import DOMPurify from 'dompurify';
import { Interweave } from 'interweave';
import React, { useState, useMemo, useCallback } from 'react';

import {
  HistoryContainer,
  FilterChipsContainer,
  FilterChip,
  HistoryContent,
  LoadingContainer,
  NoHistoryText,
  DateHeader,
  TimelineList,
  TimelineItem,
  TimelineIcon,
  TimelineContent,
  LogHeader,
  LogTimestamp,
  LogType,
  LogStatus,
  StatusTransition,
  StatusArrow,
  LogUser,
  LogNotes,
  FileAttachmentLink,
  LoadMoreButton,
} from './MobileServiceHistory.styles';
import { ServiceLogResponse } from '../../../../../@types/service.type';
import { getUploadedFilePresignedUrlByKey } from '../../../../../api/uploadedFiles';
import { isSafeUrl } from '../../../../../utils/url-validation';

interface MobileServiceHistoryProps {
  serviceLogs: ServiceLogResponse[] | undefined;
  isLoading: boolean;
}

type FilterType = 'all' | 'status' | 'notes' | 'parts' | 'events';

const INITIAL_DISPLAY_COUNT = 10;
const LOAD_MORE_COUNT = 10;

const getLogCategory = (log: ServiceLogResponse): FilterType => {
  const code = log.logTypeCode?.toLowerCase() || '';
  const name = log.logTypeName?.toLowerCase() || '';

  if (code.includes('status') || name.includes('status')) return 'status';
  if (code.includes('note') || name.includes('note')) return 'notes';
  if (code.includes('part') || name.includes('part')) return 'parts';
  return 'events';
};

const getLogIcon = (category: FilterType): React.ReactNode => {
  switch (category) {
    case 'status':
      return <SwapHoriz />;
    case 'notes':
      return <Notes />;
    case 'parts':
      return <Build />;
    default:
      return <Event />;
  }
};

const getLogTypeStyle = (category: FilterType): string => {
  switch (category) {
    case 'status':
      return 'status-change';
    case 'notes':
      return 'note';
    case 'parts':
      return 'part-action';
    default:
      return 'event';
  }
};

const formatDateHeader = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return date.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
};

interface StatusTransitionData {
  previousStatus: string | null;
  newStatus: string | null;
}

type StatusTransitionMap = Map<number, StatusTransitionData>;

export const MobileServiceHistory: React.FC<MobileServiceHistoryProps> = ({
  serviceLogs,
  isLoading,
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  const filters: { type: FilterType; label: string }[] = [
    { type: 'all', label: 'All' },
    { type: 'status', label: 'Status' },
    { type: 'notes', label: 'Notes' },
    { type: 'parts', label: 'Parts' },
    { type: 'events', label: 'Events' },
  ];

  // Pre-compute status transitions once (O(n log n)) instead of per-item (O(n^2))
  const statusTransitions = useMemo<StatusTransitionMap>(() => {
    if (!serviceLogs) return new Map();

    const statusLogs = serviceLogs
      .filter((l) => getLogCategory(l) === 'status')
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    const map = new Map<number, StatusTransitionData>();
    statusLogs.forEach((log, index) => {
      const previousLog = index > 0 ? statusLogs[index - 1] : null;
      map.set(log.id, {
        previousStatus: previousLog?.serviceStatusName || null,
        newStatus: log.serviceStatusName || null,
      });
    });

    return map;
  }, [serviceLogs]);

  // Find the initial creation log (oldest entry) — its notes duplicate the Reported Info tab
  const creationLogId = useMemo(() => {
    if (!serviceLogs || serviceLogs.length === 0) return null;
    let oldest = serviceLogs[0];
    for (const log of serviceLogs) {
      if (new Date(log.time).getTime() < new Date(oldest.time).getTime()) oldest = log;
    }
    return oldest.id;
  }, [serviceLogs]);

  const sortedAndFilteredLogs = useMemo(() => {
    if (!serviceLogs) return [];

    const sorted = [...serviceLogs]
      .filter((log) => !log.isHidden && log.id !== creationLogId)
      .sort((a, b) => {
        const timeA = new Date(a.time).getTime();
        const timeB = new Date(b.time).getTime();
        if (isNaN(timeA) && isNaN(timeB)) return 0;
        if (isNaN(timeA)) return 1;
        if (isNaN(timeB)) return -1;
        return timeB - timeA;
      });

    if (activeFilter === 'all') return sorted;
    return sorted.filter((log) => getLogCategory(log) === activeFilter);
  }, [serviceLogs, activeFilter, creationLogId]);

  const groupedLogs = useMemo(() => {
    const groups: { date: string; logs: ServiceLogResponse[] }[] = [];
    let currentDate = '';

    sortedAndFilteredLogs.slice(0, displayCount).forEach((log) => {
      const logDate = new Date(log.time);
      const dateKey = isNaN(logDate.getTime()) ? 'Unknown Date' : formatDateHeader(logDate);

      if (dateKey !== currentDate) {
        currentDate = dateKey;
        groups.push({ date: dateKey, logs: [log] });
      } else {
        groups[groups.length - 1].logs.push(log);
      }
    });

    return groups;
  }, [sortedAndFilteredLogs, displayCount]);

  const hasMoreLogs = displayCount < sortedAndFilteredLogs.length;
  const remainingCount = sortedAndFilteredLogs.length - displayCount;

  const handleLoadMore = (): void => {
    setDisplayCount((prev) => prev + LOAD_MORE_COUNT);
  };

  const handleFilterChange = (filter: FilterType): void => {
    setActiveFilter(filter);
    setDisplayCount(INITIAL_DISPLAY_COUNT);
  };

  const handleFileOpen = useCallback(async (fileKey: string): Promise<void> => {
    try {
      const fileData = await getUploadedFilePresignedUrlByKey(fileKey);
      if (fileData?.url && isSafeUrl(fileData.url)) {
        window.open(fileData.url, '_blank');
      }
    } catch {
      // Silently fail — file may no longer exist
    }
  }, []);

  return (
    <HistoryContainer>
      <FilterChipsContainer role="group" aria-label="Filter history">
        {filters.map((filter) => (
          <FilterChip
            key={filter.type}
            label={filter.label}
            isSelected={activeFilter === filter.type}
            onClick={() => handleFilterChange(filter.type)}
            aria-pressed={activeFilter === filter.type}
          />
        ))}
      </FilterChipsContainer>

      <HistoryContent>
        {isLoading ? (
          <LoadingContainer>
            <CircularProgress size={24} />
          </LoadingContainer>
        ) : sortedAndFilteredLogs.length === 0 ? (
          <NoHistoryText>
            {activeFilter === 'all' ? 'No history entries' : `No ${activeFilter} entries`}
          </NoHistoryText>
        ) : (
          <>
            {groupedLogs.map((group) => (
              <React.Fragment key={group.date}>
                <DateHeader>{group.date}</DateHeader>
                <TimelineList>
                  {group.logs.map((log) => {
                    const category = getLogCategory(log);
                    const logDate = new Date(log.time);
                    const isStatusChange = category === 'status';
                    const { previousStatus, newStatus } = isStatusChange
                      ? statusTransitions.get(log.id) || {
                          previousStatus: null,
                          newStatus: log.serviceStatusName,
                        }
                      : { previousStatus: null, newStatus: log.serviceStatusName };

                    return (
                      <TimelineItem key={log.id}>
                        <TimelineIcon logType={getLogTypeStyle(category)}>
                          {getLogIcon(category)}
                        </TimelineIcon>
                        <TimelineContent>
                          <LogHeader>
                            <LogTimestamp>
                              {isNaN(logDate.getTime()) ? 'Unknown' : formatTime(logDate)}
                            </LogTimestamp>
                            <LogType>{log.logTypeName}</LogType>
                          </LogHeader>
                          {isStatusChange && (previousStatus || newStatus) && (
                            <StatusTransition>
                              {previousStatus && <LogStatus>{previousStatus}</LogStatus>}
                              {previousStatus && newStatus && (
                                <StatusArrow>
                                  <ArrowForward fontSize="inherit" />
                                </StatusArrow>
                              )}
                              {newStatus && <LogStatus>{newStatus}</LogStatus>}
                            </StatusTransition>
                          )}
                          {!isStatusChange && log.serviceStatusName && (
                            <LogStatus>{log.serviceStatusName}</LogStatus>
                          )}
                          {log.userName && (
                            <LogUser>
                              <Person fontSize="inherit" />
                              {log.userName}
                            </LogUser>
                          )}
                          {log.notes && (
                            <LogNotes>
                              <Interweave content={DOMPurify.sanitize(log.notes)} />
                            </LogNotes>
                          )}
                          {log.xmlFileKey && (
                            <FileAttachmentLink
                              onClick={() => handleFileOpen(log.xmlFileKey!)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleFileOpen(log.xmlFileKey!);
                                }
                              }}
                            >
                              <InsertDriveFile />
                              View attached file
                            </FileAttachmentLink>
                          )}
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </TimelineList>
              </React.Fragment>
            ))}

            {hasMoreLogs && (
              <LoadMoreButton variant="outlined" onClick={handleLoadMore}>
                Load {Math.min(remainingCount, LOAD_MORE_COUNT)} more
                {remainingCount > LOAD_MORE_COUNT && ` (${remainingCount} remaining)`}
              </LoadMoreButton>
            )}
          </>
        )}
      </HistoryContent>
    </HistoryContainer>
  );
};
